/**
 * The Upload screen's waveforms, drawn entirely on canvas (bars coloured by sound region, scene-cut
 * markers, snippet ranges, the selection with its free handles, word labels and the playhead), so no
 * element ever needs a computed inline position. Pointer maths: x -> ms across the visible window.
 */
import { useCallback, useEffect, useRef } from 'react';
import { PEAKS_PER_SECOND } from '@studio/contracts';

export interface WaveRegion { kind: string; startMs: number; endMs: number }
export interface WaveWord { word: string; startMs: number; endMs: number }
export interface WaveRange { id: string; inMs: number; outMs: number; colour: string; excluded: boolean }

export interface WaveformProps {
  peaks: number[];
  durationMs: number;
  windowStartMs?: number;
  windowEndMs?: number;
  regions: WaveRegion[];
  words: WaveWord[];
  sceneCuts: number[];
  /** Where one file ends and the next begins in a project; drawn as a marker, named on hover. */
  joins?: { atMs: number; name: string }[];
  ranges: WaveRange[];
  selectedId: string | null;
  /** Staged in/out for the selected range while its handles are being dragged. */
  staged: { inMs: number; outMs: number } | null;
  playheadMs: number | null;
  height: number;
  showWords?: boolean;
  onSelect?: (id: string) => void;
  onEdge?: (which: 'in' | 'out', ms: number, done: boolean) => void;
  /**
   * Dragging the selected range's body: `deltaMs` along the waveform, later to the right, `done` on
   * release. The Composer's slip, the same length of footage from earlier or later (Dax, 2026-09-29).
   */
  onSlip?: (deltaMs: number, done: boolean) => void;
  onSeek?: (ms: number) => void;
}

const HANDLE_HIT_PX = 8;

function cssVar(el: HTMLElement, name: string, fallback: string): string {
  return getComputedStyle(el).getPropertyValue(name).trim() || fallback;
}

/**
 * Display auto-gain: quiet clips amplify until their shape is visible, loud ones stay put so their
 * detail is not clipped flat. Scaled to the window's 95th percentile; display only, never the audio.
 * Sampled (at most ~400 points) and cached per peaks array and window, because this runs on every
 * redraw and a drag redraws on every pointer move.
 */
const GAIN_SAMPLE_CAP = 400;
const gainCache = new WeakMap<number[], Map<string, number>>();

export function displayGain(peaks: number[], from: number, to: number): number {
  const lo = Math.max(0, from);
  const hi = Math.min(peaks.length, Math.max(lo + 1, to));
  const key = `${lo}:${hi}`;
  let perArray = gainCache.get(peaks);
  if (perArray?.has(key)) return perArray.get(key)!;
  const len = hi - lo;
  const stride = Math.max(1, Math.floor(len / GAIN_SAMPLE_CAP));
  const sampled: number[] = [];
  for (let i = lo; i < hi; i += stride) sampled.push(peaks[i] ?? 0);
  sampled.sort((a, b) => a - b);
  const p95 = sampled[Math.floor(sampled.length * 0.95)] ?? 0;
  const gain = p95 <= 0 ? 1 : Math.min(12, Math.max(1, 0.9 / p95));
  if (!perArray) {
    perArray = new Map();
    gainCache.set(peaks, perArray);
  }
  perArray.set(key, gain);
  return gain;
}

export function Waveform(props: WaveformProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dragRef = useRef<'in' | 'out' | null>(null);
  const slipRef = useRef<number | null>(null);
  /** Where the pointer was last seen while dragging: where a drag whose release was lost ends. */
  const lastX = useRef(0);
  const winStart = props.windowStartMs ?? 0;
  const winEnd = props.windowEndMs ?? props.durationMs;
  const span = Math.max(1, winEnd - winStart);

  const msToX = useCallback((ms: number, width: number) => ((ms - winStart) / span) * width, [winStart, span]);
  const xToMs = useCallback((x: number, width: number) => Math.round(winStart + (x / width) * span), [winStart, span]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = props.height;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    const col = {
      bar: cssVar(canvas, '--wf-bar', '#d5d5da'),
      speech: cssVar(canvas, '--wf-speech', '#0a6fe0'),
      noise: cssVar(canvas, '--wf-noise', '#d9b48f'),
      cut: cssVar(canvas, '--wf-cut', 'rgba(0,0,0,0.35)'),
      join: cssVar(canvas, '--wf-join', 'rgba(37,99,235,0.7)'),
      word: cssVar(canvas, '--wf-word', '#a8a8ad'),
      playhead: cssVar(canvas, '--danger', '#d9534f'),
    };
    ctx.clearRect(0, 0, w, h);

    const kindAt = (ms: number): string => props.regions.find((r) => ms >= r.startMs && ms < r.endMs)?.kind ?? 'quiet';
    const barW = 3;
    const gap = 1;
    const bars = Math.floor(w / (barW + gap));
    const wordsPad = props.showWords ? 21 : 0;
    const baseline = h - wordsPad;
    const gain = displayGain(props.peaks, Math.floor((winStart / 1000) * PEAKS_PER_SECOND), Math.ceil((winEnd / 1000) * PEAKS_PER_SECOND));
    for (let b = 0; b < bars; b++) {
      const t0 = winStart + (b / bars) * span;
      const t1 = winStart + ((b + 1) / bars) * span;
      let peak = 0;
      const i0 = Math.max(0, Math.floor((t0 / 1000) * PEAKS_PER_SECOND));
      const i1 = Math.min(props.peaks.length, Math.ceil((t1 / 1000) * PEAKS_PER_SECOND));
      for (let i = i0; i < i1; i++) peak = Math.max(peak, props.peaks[i] ?? 0);
      const kind = kindAt((t0 + t1) / 2);
      ctx.fillStyle = kind === 'speech' ? col.speech : kind === 'noise' ? col.noise : col.bar;
      const bh = Math.max(2, Math.min(1, peak * gain) * (baseline - 6));
      ctx.fillRect(b * (barW + gap), baseline - bh, barW, bh);
    }

    for (const cut of props.sceneCuts) {
      if (cut < winStart || cut > winEnd) continue;
      const x = msToX(cut, w);
      ctx.fillStyle = col.cut;
      ctx.fillRect(x, 0, 1, baseline);
    }
    for (const join of props.joins ?? []) {
      if (join.atMs < winStart || join.atMs > winEnd) continue;
      const x = msToX(join.atMs, w);
      ctx.fillStyle = col.join;
      ctx.fillRect(x - 1, 0, 2, h);
    }

    // Multiply blending: where two ranges overlap the fills mix into a visibly different colour, so an
    // overlap (which is allowed, and sometimes wanted) is always evident.
    ctx.globalCompositeOperation = 'multiply';
    for (const r of props.ranges) {
      if (r.outMs < winStart || r.inMs > winEnd) continue;
      const x0 = Math.max(0, msToX(r.id === props.selectedId ? (props.staged?.inMs ?? r.inMs) : r.inMs, w));
      const x1 = Math.min(w, msToX(r.id === props.selectedId ? (props.staged?.outMs ?? r.outMs) : r.outMs, w));
      ctx.globalAlpha = r.excluded ? 0.15 : r.id === props.selectedId ? 0.3 : 0.2;
      ctx.fillStyle = r.colour;
      ctx.fillRect(x0, 0, x1 - x0, baseline);
      ctx.globalAlpha = 1;
    }
    ctx.globalCompositeOperation = 'source-over';

    const selected = props.ranges.find((r) => r.id === props.selectedId);
    if (selected) {
      const inMs = props.staged?.inMs ?? selected.inMs;
      const outMs = props.staged?.outMs ?? selected.outMs;
      const x0 = msToX(inMs, w);
      const x1 = msToX(outMs, w);
      // A slim boundary on the active range: single-pixel top and bottom, two-pixel side handles.
      ctx.fillStyle = selected.colour;
      ctx.fillRect(x0, 0, Math.max(1, x1 - x0), 1);
      ctx.fillRect(x0, baseline - 1, Math.max(1, x1 - x0), 1);
      for (const x of [x0, x1]) ctx.fillRect(x - 1, 0, 2, baseline);
    }

    if (props.showWords) {
      // Two staggered rows so neighbouring words drop to the lower line instead of being skipped;
      // each word's left edge stays tied to where it is spoken.
      ctx.font = '8px system-ui, sans-serif';
      ctx.fillStyle = col.word;
      const rowEnds = [-Infinity, -Infinity];
      for (const word of props.words) {
        if (word.startMs < winStart || word.startMs > winEnd) continue;
        const x = msToX(word.startMs, w);
        const width = ctx.measureText(word.word).width;
        const row = x >= rowEnds[0]! + 2 ? 0 : x >= rowEnds[1]! + 2 ? 1 : -1;
        if (row === -1) continue;
        ctx.fillText(word.word, x, row === 0 ? h - 11 : h - 2);
        rowEnds[row] = x + width;
      }
    }

    if (props.playheadMs !== null && props.playheadMs >= winStart && props.playheadMs <= winEnd) {
      ctx.fillStyle = col.playhead;
      ctx.fillRect(msToX(props.playheadMs, w), 0, 1.5, baseline);
    }
  }, [props, winStart, winEnd, span, msToX]);

  useEffect(() => {
    draw();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ro = new ResizeObserver(draw);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, [draw]);

  const hitHandle = (x: number, width: number): 'in' | 'out' | null => {
    const selected = props.ranges.find((r) => r.id === props.selectedId);
    if (!selected) return null;
    const inX = msToX(props.staged?.inMs ?? selected.inMs, width);
    const outX = msToX(props.staged?.outMs ?? selected.outMs, width);
    if (Math.abs(x - inX) <= HANDLE_HIT_PX) return 'in';
    if (Math.abs(x - outX) <= HANDLE_HIT_PX) return 'out';
    return null;
  };

  /** Inside the selected range, clear of its handles: where a slip is grabbed. */
  const insideSelected = (ms: number) => {
    const selected = props.ranges.find((r) => r.id === props.selectedId);
    if (!selected) return false;
    return ms > (props.staged?.inMs ?? selected.inMs) && ms < (props.staged?.outMs ?? selected.outMs);
  };

  const pointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    lastX.current = e.clientX;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const handle = hitHandle(x, rect.width);
    if (handle && props.onEdge) {
      dragRef.current = handle;
      canvas.setPointerCapture(e.pointerId);
      return;
    }
    const ms = xToMs(x, rect.width);
    if (props.onSlip && insideSelected(ms)) {
      slipRef.current = e.clientX;
      canvas.setPointerCapture(e.pointerId);
      return;
    }
    const hitRange = props.ranges.find((r) => !r.excluded && ms >= r.inMs && ms <= r.outMs);
    if (hitRange && props.onSelect) props.onSelect(hitRange.id);
    else if (props.onSeek) props.onSeek(ms);
  };
  const pointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // No button held: the release was lost (past the edge, or the capture dropped); the drag ends here.
    if ((dragRef.current || slipRef.current !== null) && e.buttons === 0) return finish(lastX.current);
    if (dragRef.current || slipRef.current !== null) lastX.current = e.clientX;
    const rect = canvas.getBoundingClientRect();
    if (dragRef.current && props.onEdge) {
      props.onEdge(dragRef.current, Math.max(0, Math.min(props.durationMs, xToMs(e.clientX - rect.left, rect.width))), false);
    } else if (slipRef.current !== null && props.onSlip) {
      props.onSlip(((e.clientX - slipRef.current) / rect.width) * span, false);
    } else {
      const x = e.clientX - rect.left;
      canvas.dataset['cursor'] = hitHandle(x, rect.width) ? 'ew' : props.onSlip && insideSelected(xToMs(x, rect.width)) ? 'grab' : 'default';
      const join = (props.joins ?? []).find((j) => Math.abs(msToX(j.atMs, rect.width) - x) <= HANDLE_HIT_PX);
      canvas.title = join ? `${join.name} begins here` : '';
    }
  };
  /**
   * A drag ends at the release, wherever it happens: past the waveform's edge it lands on the edge (the time is
   * clamped), and a release the waveform never heard (capture lost) ends it at the last place it was seen.
   */
  const finish = (clientX: number) => {
    const canvas = canvasRef.current;
    if (canvas && slipRef.current !== null && props.onSlip) {
      const from = slipRef.current;
      slipRef.current = null;
      props.onSlip(((clientX - from) / canvas.getBoundingClientRect().width) * span, true);
      return;
    }
    const edge = dragRef.current;
    dragRef.current = null;
    if (!canvas || !edge || !props.onEdge) return;
    const rect = canvas.getBoundingClientRect();
    props.onEdge(edge, Math.max(0, Math.min(props.durationMs, xToMs(clientX - rect.left, rect.width))), true);
  };
  const pointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => finish(e.clientX);

  return (
    <canvas
      ref={canvasRef}
      className="wave-canvas"
      role="img"
      aria-label="Audio waveform"
      onPointerDown={pointerDown}
      onPointerMove={pointerMove}
      onPointerUp={pointerUp}
      onPointerCancel={() => finish(lastX.current)}
      onLostPointerCapture={() => finish(lastX.current)}
    />
  );
}

/** The thin all-snippets strip under the whole-clip waveform; its blocks click like the ranges above. */
export function BlockStrip({ ranges, durationMs, selectedId, onSelect }: { ranges: WaveRange[]; durationMs: number; selectedId: string | null; onSelect?: (id: string) => void }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, w, h);
    for (const r of ranges) {
      ctx.globalAlpha = r.id === selectedId ? 1 : 0.55;
      ctx.fillStyle = r.excluded ? '#d5d5da' : r.colour;
      const x0 = (r.inMs / durationMs) * w;
      const x1 = (r.outMs / durationMs) * w;
      ctx.fillRect(x0, 0, Math.max(2, x1 - x0), h);
    }
    ctx.globalAlpha = 1;
  }, [ranges, durationMs, selectedId]);
  const click = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = ref.current;
    if (!canvas || !onSelect) return;
    const rect = canvas.getBoundingClientRect();
    const ms = ((e.clientX - rect.left) / rect.width) * durationMs;
    const hit = ranges.find((r) => !r.excluded && ms >= r.inMs && ms <= r.outMs);
    if (hit) onSelect(hit.id);
  };
  return <canvas ref={ref} className={`block-strip${onSelect ? ' clickable' : ''}`} role={onSelect ? 'button' : 'img'} aria-label="Snippet ranges" onPointerDown={click} />;
}

/** A row's miniature waveform with its range highlighted. */
export function MiniWave({ peaks, durationMs, inMs, outMs, colour, regions }: { peaks: number[]; durationMs: number; inMs: number; outMs: number; colour: string; regions: WaveRegion[] }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, w, h);
    const speech = cssVar(canvas, '--wf-speech', '#0a6fe0');
    const barCol = cssVar(canvas, '--wf-bar', '#d5d5da');
    const bars = Math.floor(w / 3);
    const gain = displayGain(peaks, 0, peaks.length);
    for (let b = 0; b < bars; b++) {
      const t = (b / bars) * durationMs;
      let peak = 0;
      const i0 = Math.floor((t / 1000) * PEAKS_PER_SECOND);
      const i1 = Math.min(peaks.length, Math.ceil((((b + 1) / bars) * durationMs / 1000) * PEAKS_PER_SECOND));
      for (let i = i0; i < i1; i++) peak = Math.max(peak, peaks[i] ?? 0);
      const inSpeech = regions.some((r) => r.kind === 'speech' && t >= r.startMs && t < r.endMs);
      ctx.fillStyle = inSpeech ? speech : barCol;
      const bh = Math.max(1, Math.min(1, peak * gain) * (h - 2));
      ctx.fillRect(b * 3, h - bh, 2, bh);
    }
    ctx.strokeStyle = colour;
    ctx.lineWidth = 1.5;
    const x0 = (inMs / durationMs) * w;
    const x1 = (outMs / durationMs) * w;
    ctx.strokeRect(x0, 0.75, Math.max(2, x1 - x0), h - 1.5);
  }, [peaks, durationMs, inMs, outMs, colour, regions]);
  return <canvas ref={ref} className="mini-wave" role="img" aria-label="Snippet waveform" />;
}
